import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast, Toaster } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { Plus, Trash2, RefreshCw, ExternalLink, Library, Image as ImageIcon } from "lucide-react";
import TipTapEditor from "@/components/editor/TipTapEditor";
import MediaLibraryModal from "@/components/editor/MediaLibraryModal";

const empty = () => ({
  slug: "",
  cover_image: "",
  video_url: "",
  publish_date: new Date().toISOString().slice(0, 10),
  pinned: false,
  comments_enabled: true,
  status: "published",
  ar: { title: "", excerpt: "", body: "" },
  en: { title: "", excerpt: "", body: "" },
});

const AdminArticles = () => {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null = list view, otherwise article object
  const [tab, setTab] = useState("ar");
  const [showLibrary, setShowLibrary] = useState(false);
  const [showCoverPicker, setShowCoverPicker] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/articles");
      setItems(data.items || []);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);

  const onSave = async (e) => {
    e.preventDefault();
    const payload = {
      slug: editing.slug || null,
      cover_image: editing.cover_image || null,
      video_url: editing.video_url || null,
      publish_date: editing.publish_date ? new Date(editing.publish_date).toISOString() : null,
      pinned: !!editing.pinned,
      comments_enabled: !!editing.comments_enabled,
      status: editing.status,
      ar: editing.ar,
      en: editing.en,
    };
    try {
      if (editing.id) {
        await api.patch(`/admin/articles/${editing.id}`, payload);
        toast.success("تم تحديث المقال");
      } else {
        await api.post("/admin/articles", payload);
        toast.success("تم إنشاء المقال");
      }
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const remove = async (a) => {
    if (!window.confirm(`حذف "${a.ar?.title || a.slug}"؟`)) return;
    try {
      await api.delete(`/admin/articles/${a.id}`);
      toast.success("تم الحذف");
      load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  if (editing) {
    return (
      <div data-testid="admin-article-editor">
        <Toaster position="top-left" theme="dark" richColors />
        <div className="flex items-center justify-between mb-6">
          <button onClick={() => setEditing(null)} className="overline text-white/60 hover:text-gold">← العودة للقائمة</button>
          <button
            type="button"
            onClick={() => setShowLibrary(true)}
            className="inline-flex items-center gap-2 border border-white/15 hover:border-gold hover:text-gold text-bone text-xs tracking-[0.2em] uppercase px-4 py-2 transition-colors"
            data-testid="open-media-library"
          >
            <Library size={14} />
            <span className="font-arabic tracking-normal">المكتبة</span>
          </button>
        </div>

        <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone mb-10">{editing.id ? "تعديل مقال" : "مقال جديد"}</h1>

        <form onSubmit={onSave} className="space-y-8">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Input label="Slug (اختياري)" value={editing.slug || ""} onChange={(v) => setEditing({ ...editing, slug: v })} ltr />
            <Input label="تاريخ النشر" type="date" value={(editing.publish_date || "").slice(0, 10)} onChange={(v) => setEditing({ ...editing, publish_date: v })} />
            <div className="md:col-span-2">
              <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">رابط الصورة الرئيسية</span>
              <div className="flex gap-3">
                <input
                  type="text"
                  value={editing.cover_image || ""}
                  onChange={(e) => setEditing({ ...editing, cover_image: e.target.value })}
                  className="editorial-input flex-1"
                  dir="ltr"
                  placeholder="https://… أو اختر من المكتبة"
                />
                <button type="button" onClick={() => setShowCoverPicker(true)} className="border border-white/15 hover:border-gold hover:text-gold px-4 inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase" data-testid="cover-pick-from-library">
                  <ImageIcon size={14} /><span className="font-arabic tracking-normal">من المكتبة</span>
                </button>
              </div>
              {editing.cover_image && (
                <img src={editing.cover_image} alt="" className="mt-3 max-h-40 w-auto border border-white/10" onError={(e) => { e.currentTarget.style.display = "none"; }} />
              )}
            </div>
            <Input label="رابط الفيديو (YouTube/Vimeo/مباشر)" value={editing.video_url || ""} onChange={(v) => setEditing({ ...editing, video_url: v })} ltr />
          </div>

          <div className="flex flex-wrap items-center gap-6 text-sm font-arabic">
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!editing.pinned} onChange={(e) => setEditing({ ...editing, pinned: e.target.checked })} className="accent-gold" />
              تثبيت في الرئيسية
            </label>
            <label className="flex items-center gap-3 cursor-pointer">
              <input type="checkbox" checked={!!editing.comments_enabled} onChange={(e) => setEditing({ ...editing, comments_enabled: e.target.checked })} className="accent-gold" />
              السماح بالتعليقات
            </label>
            <label className="flex items-center gap-3">
              <span className="text-white/50">الحالة:</span>
              <select value={editing.status} onChange={(e) => setEditing({ ...editing, status: e.target.value })} className="bg-ink-soft border border-white/15 px-3 py-1.5">
                <option value="draft">مسودة</option>
                <option value="published">منشور</option>
              </select>
            </label>
          </div>

          {/* Locale tabs */}
          <div className="flex gap-2 border-b border-white/10">
            {["ar", "en"].map((l) => (
              <button key={l} type="button" onClick={() => setTab(l)} className={`px-5 py-2 overline ${tab === l ? "text-gold border-b-2 border-gold" : "text-white/50 hover:text-white"}`}>
                {l === "ar" ? "العربية" : "English"}
              </button>
            ))}
          </div>

          <div className="space-y-6">
            <Input label="العنوان" value={editing[tab].title} onChange={(v) => setEditing({ ...editing, [tab]: { ...editing[tab], title: v } })} />
            <Textarea rows={2} label="ملخّص" value={editing[tab].excerpt} onChange={(v) => setEditing({ ...editing, [tab]: { ...editing[tab], excerpt: v } })} />
            <div>
              <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">نص المقال</span>
              <TipTapEditor
                value={editing[tab].body}
                onChange={(html) => setEditing({ ...editing, [tab]: { ...editing[tab], body: html } })}
                dir={tab === "ar" ? "rtl" : "ltr"}
                placeholder={tab === "ar" ? "ابدأ الكتابة هنا…" : "Start writing here…"}
              />
            </div>
          </div>

          <div className="flex gap-4">
            <button type="submit" className="btn-gold" data-testid="article-save">{editing.id ? "حفظ التغييرات" : "نشر المقال"}</button>
            <button type="button" onClick={() => setEditing(null)} className="btn-ghost">إلغاء</button>
          </div>
        </form>

        {/* Standalone media library (top icon) */}
        <MediaLibraryModal
          open={showLibrary}
          onClose={() => setShowLibrary(false)}
          onSelect={(it) => {
            navigator.clipboard?.writeText(it.url).catch(() => {});
            toast.success("تم نسخ رابط الوسيط");
            setShowLibrary(false);
          }}
        />
        {/* Cover picker */}
        <MediaLibraryModal
          open={showCoverPicker}
          kind="image"
          onClose={() => setShowCoverPicker(false)}
          onSelect={(it) => {
            setEditing((cur) => ({ ...cur, cover_image: it.url }));
            setShowCoverPicker(false);
          }}
        />
      </div>
    );
  }

  return (
    <div data-testid="admin-articles">
      <Toaster position="top-left" theme="dark" richColors />
      <div className="flex items-center justify-between mb-10">
        <div>
          <div className="overline mb-3">المقالات</div>
          <h1 className="font-arabic font-black text-3xl sm:text-4xl text-bone">إدارة المقالات</h1>
        </div>
        <div className="flex gap-3">
          <button onClick={load} className="btn-ghost"><RefreshCw size={14} /></button>
          <button onClick={() => { setEditing(empty()); setTab("ar"); }} className="btn-gold" data-testid="article-new"><Plus size={14} />مقال جديد</button>
        </div>
      </div>

      <div className="border border-white/10 overflow-x-auto">
        <table className="w-full text-sm">
          <thead className="bg-ink-soft text-white/50 uppercase tracking-[0.2em] text-xs">
            <tr>
              <th className="px-4 py-3 text-right font-normal">العنوان</th>
              <th className="px-4 py-3 text-right font-normal">التاريخ</th>
              <th className="px-4 py-3 text-right font-normal">الحالة</th>
              <th className="px-4 py-3 text-right font-normal">المشاهدات</th>
              <th className="px-4 py-3 text-right font-normal">إجراءات</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/10">
            {loading && <tr><td colSpan={5} className="p-6 text-center text-white/40">…</td></tr>}
            {!loading && items.length === 0 && <tr><td colSpan={5} className="p-6 text-center text-white/40 font-arabic">لا توجد مقالات بعد. ابدأ بكتابة أول مقال.</td></tr>}
            {items.map((a) => (
              <tr key={a.id} className="hover:bg-white/5" data-testid={`article-row-${a.id}`}>
                <td className="px-4 py-3 font-arabic max-w-md truncate">{a.ar?.title || a.en?.title || a.slug}</td>
                <td className="px-4 py-3 text-white/45 text-xs" dir="ltr">{(a.publish_date || "").slice(0, 10)}</td>
                <td className="px-4 py-3"><span className={`text-xs uppercase tracking-[0.2em] ${a.status === "published" ? "text-emerald-400" : "text-amber-400"}`}>{a.status === "published" ? "منشور" : "مسودة"}</span></td>
                <td className="px-4 py-3 text-gold tabular-nums">{a.views || 0}</td>
                <td className="px-4 py-3">
                  <div className="flex gap-3">
                    <button onClick={() => { setEditing(a); setTab("ar"); }} className="text-white/60 hover:text-gold overline" data-testid={`article-edit-${a.id}`}>تعديل</button>
                    <Link to={`/articles/${a.slug}`} target="_blank" className="text-white/60 hover:text-gold" title="عرض"><ExternalLink size={14} /></Link>
                    <button onClick={() => remove(a)} className="text-white/60 hover:text-red-400" data-testid={`article-delete-${a.id}`}><Trash2 size={14} /></button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
};

const Input = ({ label, value, onChange, type = "text", ltr }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <input type={type} value={value || ""} onChange={(e) => onChange(e.target.value)} className="editorial-input" dir={ltr ? "ltr" : "auto"} />
  </label>
);

const Textarea = ({ label, value, onChange, rows = 4 }) => (
  <label className="block">
    <span className="block mb-2 text-xs tracking-[0.2em] uppercase text-white/50 font-arabic">{label}</span>
    <textarea rows={rows} value={value || ""} onChange={(e) => onChange(e.target.value)} className="editorial-input resize-none font-arabic leading-[1.9]" />
  </label>
);

export default AdminArticles;
